import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaCheck, FaMagic, FaSpinner } from 'react-icons/fa';
import Button from '../../common/Button';
import PricingSuggestionPanel from '../../productos/PricingSuggestionPanel';
import TipoVentaProductoBlock from './TipoVentaProductoBlock';
import { normalizePluBalanza } from '../../../utils/balanza/pluBalanza';
import { TIPO_VENTA_MODO_PESO } from '../../../utils/productoTipoVentaAdmin';

const STEPS = [
  { id: 1, label: 'Nombre', hint: 'Identificación' },
  { id: 2, label: 'Precios', hint: 'Costo y venta' },
  { id: 3, label: 'Detalles', hint: 'Categoría y más' },
  { id: 4, label: 'Confirmar', hint: 'Guardar' }
];

const fieldClass = 'nexo-field mt-1';
const labelClass = 'text-sm font-semibold text-e0e3-foreground';
const hintClass = 'mt-1.5 text-xs text-e0e3-muted';

/**
 * Asistente de pasos para crear un producto (solo alta): incluye precio de costo, venta y margen manual / por %.
 */
export default function ProductoFormWizard({
  wizardStep,
  avanzarWizard,
  retrocederWizard,
  formData,
  producto,
  handleChange,
  categorias = [],
  proveedores,
  modoCalculo,
  setModoCalculo,
  margenGanancia,
  handlePrecioChange,
  handleMargenChange,
  sugerencia,
  pricingSuggestionConfig,
  onPricingSuggestionConfigChange,
  onAplicarPrecioSugerido,
  onSyncFinanzas,
  syncingGastosFinanzas = false,
  canUsePricingSuggestion = false,
  guardarProducto,
  submitting,
  navigate,
  onCodigoBlur,
  onPluBalanzaBlur,
  onTipoVentaModoChange,
  barcodeLookupLoading = false
}) {
  const pct = wizardStep <= 4 ? Math.round((wizardStep / 4) * 100) : 100;

  return (
    <div className="nexo-surface overflow-hidden shadow-elevated">
      <div className="h-1.5 w-full bg-white/[0.06]">
        <div
          className="h-full rounded-r-full bg-gradient-to-r from-e0e3-accent via-e0e3-accentSoft to-e0e3-accent transition-[width] duration-300 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="relative overflow-hidden border-b border-white/10 bg-e0e3-accent/10 px-5 pb-8 pt-5 sm:px-6">
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25">
              <FaMagic className="text-lg" aria-hidden />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-e0e3-foreground sm:text-2xl">Nuevo producto</h1>
              <p className="mt-0.5 text-sm text-e0e3-muted">Paso a paso. Siempre podés volver atrás.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/productos')}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.06] px-4 py-2 text-sm font-medium text-e0e3-foreground transition hover:bg-white/[0.1]"
          >
            <FaArrowLeft />
            Salir
          </button>
        </div>

        <div className="relative mt-6 flex justify-between gap-1 sm:gap-2">
          {STEPS.map((s, i) => {
            const active = wizardStep === s.id;
            const done = wizardStep > s.id;
            return (
              <React.Fragment key={s.id}>
                <div className="flex min-w-0 flex-1 flex-col items-center text-center">
                  <div
                    className={[
                      'flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold sm:h-10 sm:w-10 sm:text-sm',
                      done
                        ? 'bg-emerald-400 text-emerald-950 shadow-md'
                        : active
                          ? 'bg-e0e3-accent text-white shadow-lg ring-2 ring-e0e3-accent/40'
                          : 'bg-white/[0.08] text-e0e3-muted ring-1 ring-white/10'
                    ].join(' ')}
                  >
                    {done ? <FaCheck className="text-xs" /> : s.id}
                  </div>
                  <p
                    className={`mt-1.5 truncate text-[11px] font-semibold sm:text-xs ${active || done ? 'text-e0e3-foreground' : 'text-e0e3-muted'}`}
                  >
                    {s.label}
                  </p>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="mb-6 hidden w-2 shrink-0 rounded-full bg-white/10 sm:block md:w-6" aria-hidden />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="min-h-[280px] bg-e0e3-surface px-5 py-6 sm:px-8 sm:py-8">
        {wizardStep === 1 && (
          <div className="mx-auto max-w-md space-y-5 animate-[wizIn_0.25s_ease-out]">
            <style>{`@keyframes wizIn { from { opacity:0; transform: translateY(6px);} to { opacity:1; transform:none;} }`}</style>
            <div>
              <label className={labelClass}>Nombre del producto *</label>
              <input
                type="text"
                name="nombre"
                value={producto.nombre}
                onChange={handleChange}
                className={fieldClass}
                placeholder="Ej: Yerba mate 500 g"
                autoComplete="off"
              />
            </div>
            <div>
              <label className={labelClass}>Código (opcional)</label>
              <input
                type="text"
                name="codigo"
                value={producto.codigo}
                onChange={handleChange}
                onBlur={typeof onCodigoBlur === 'function' ? onCodigoBlur : undefined}
                className={fieldClass}
                placeholder="Si lo dejás vacío, lo generamos nosotros"
                autoComplete="off"
              />
              <p className={hintClass}>
                Con 8–14 dígitos: buscamos en Firebase, catálogo comunitario 0E3 POS, caché, Open Food Facts y UPCitemdb. Si hay
                datos, se puede crear el producto al salir del campo; los precios los cargás en el paso siguiente o en la ficha
                completa.
              </p>
              {barcodeLookupLoading && (
                <p className="mt-1 text-xs font-medium text-e0e3-accent-soft">Buscando código…</p>
              )}
            </div>
            <TipoVentaProductoBlock
              tipoVentaModo={producto.tipoVentaModo}
              onTipoVentaModoChange={onTipoVentaModoChange}
              pluBalanza={producto.pluBalanza || ''}
              onPluChange={handleChange}
              onPluBlur={typeof onPluBalanzaBlur === 'function' ? onPluBalanzaBlur : undefined}
              fieldClass={fieldClass}
              labelClassName={labelClass}
              radioLabelClass="inline-flex items-center gap-2 text-sm text-e0e3-muted"
            />
          </div>
        )}

        {wizardStep === 2 && (
          <div className="mx-auto max-w-lg space-y-4 animate-[wizIn_0.25s_ease-out]">
            <style>{`@keyframes wizIn { from { opacity:0; transform: translateY(6px);} to { opacity:1; transform:none;} }`}</style>
            <p className="text-sm text-e0e3-muted">
              Costo y venta. Podés igualar margen manualmente o establecer el % sobre el costo.
            </p>
            <div>
              <label className={labelClass}>Precio de costo *</label>
              <div className="relative mt-1">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-e0e3-muted">$</span>
                <input
                  type="number"
                  name="precio_costo"
                  value={producto.precio_costo}
                  onChange={handlePrecioChange}
                  className={`${fieldClass} pl-8`}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
            <div>
              <span className={labelClass}>Modo de cálculo</span>
              <div className="mt-2 flex gap-4">
                <label className="inline-flex items-center gap-2 text-sm text-e0e3-muted">
                  <input
                    type="radio"
                    name="modoCalculoWiz"
                    checked={modoCalculo === 'manual'}
                    onChange={() => setModoCalculo('manual')}
                    className="text-e0e3-accent"
                  />
                  Manual
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-e0e3-muted">
                  <input
                    type="radio"
                    name="modoCalculoWiz"
                    checked={modoCalculo === 'porcentaje'}
                    onChange={() => setModoCalculo('porcentaje')}
                    className="text-e0e3-accent"
                  />
                  Por margen %
                </label>
              </div>
            </div>
            <div>
              <label className={labelClass}>
                {formData.tipoVentaModo === TIPO_VENTA_MODO_PESO
                  ? 'Precio de venta (por kg) *'
                  : 'Precio de venta *'}
              </label>
              <div className="relative mt-1">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-e0e3-muted">$</span>
                <input
                  type="number"
                  name="precio_venta"
                  value={producto.precio_venta}
                  onChange={handlePrecioChange}
                  className={`${fieldClass} pl-8 ${modoCalculo === 'porcentaje' ? 'bg-white/[0.06]' : ''}`}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
              {formData.tipoVentaModo === TIPO_VENTA_MODO_PESO && (
                <p className={hintClass}>Este importe se usa como precio por kilogramo en caja y balanza.</p>
              )}
              {canUsePricingSuggestion && sugerencia?.canSuggest && (
                <div className="mt-2 rounded-lg border border-e0e3-accent/30 bg-e0e3-accent/10 px-3 py-2 text-xs text-e0e3-foreground">
                  <p>
                    Sugerido: <strong>${sugerencia.suggestedPrice.toFixed(2)}</strong>
                    {' · '}Costo unitario base: ${sugerencia.costoTotalUnitario.toFixed(2)}
                    {!sugerencia.incluyeGastosFijos && sugerencia.gastosMensuales > 0
                      ? ' (sin prorrateo — indicá unidades/mes)'
                      : ''}
                  </p>
                  <button
                    type="button"
                    onClick={onAplicarPrecioSugerido}
                    className="mt-2 rounded-md border border-e0e3-accent/40 bg-e0e3-accent/10 px-2.5 py-1 font-semibold text-e0e3-accent-soft hover:bg-e0e3-accent/20"
                  >
                    Usar sugerido
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className={labelClass}>Margen de ganancia (%)</label>
              <div className="relative mt-1">
                <input
                  type="number"
                  value={margenGanancia}
                  onChange={handleMargenChange}
                  className={`${fieldClass} pr-10 ${modoCalculo === 'manual' ? 'bg-white/[0.06]' : ''}`}
                  step="0.01"
                  placeholder="0"
                />
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-e0e3-muted">%</span>
              </div>
            </div>
            <p className="text-xs text-e0e3-muted">
              Las tres listas de precio se igualan al precio de venta hasta que las edites en{' '}
              <Link to="/productos/precios" className="font-medium text-e0e3-accent-soft hover:underline">
                Gestión de precios
              </Link>
              .
            </p>
            {canUsePricingSuggestion && (
              <PricingSuggestionPanel
                compact
                pricingSuggestionConfig={pricingSuggestionConfig}
                onConfigChange={onPricingSuggestionConfigChange}
                sugerencia={sugerencia}
                onSyncFinanzas={onSyncFinanzas}
                syncingFinanzas={syncingGastosFinanzas}
              />
            )}
          </div>
        )}

        {wizardStep === 3 && (
          <div className="mx-auto max-w-lg space-y-4 animate-[wizIn_0.25s_ease-out]">
            <style>{`@keyframes wizIn { from { opacity:0; transform: translateY(6px);} to { opacity:1; transform:none;} }`}</style>
            <p className="text-sm text-e0e3-muted">Opcional: podés completarlo después en la ficha del producto.</p>
            <div>
              <label className={labelClass}>Descripción</label>
              <textarea
                name="descripcion"
                value={producto.descripcion}
                onChange={handleChange}
                rows={3}
                className={fieldClass}
              />
            </div>
            <div>
              <label className={labelClass}>Categoría</label>
              <select name="categoria_id" value={producto.categoria_id} onChange={handleChange} className={fieldClass}>
                <option value="">Seleccionar categoría</option>
                {(categorias || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
              <p className={hintClass}>Creá nuevas desde Productos → Categorías.</p>
            </div>
            <div>
              <label className={labelClass}>Proveedor</label>
              <select name="proveedor_id" value={producto.proveedor_id} onChange={handleChange} className={fieldClass}>
                <option value="">Sin proveedor</option>
                {proveedores.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Stock mínimo</label>
              <input
                type="number"
                name="stock_minimo"
                value={producto.stock_minimo}
                onChange={handleChange}
                className={fieldClass}
                min="0"
              />
            </div>
            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5">
              <input
                type="checkbox"
                name="activo"
                checked={producto.activo}
                onChange={handleChange}
                className="h-4 w-4 rounded border-white/20 text-e0e3-accent focus:ring-e0e3-accent"
              />
              <span className="text-sm font-medium text-e0e3-foreground">Producto activo</span>
            </label>
          </div>
        )}

        {wizardStep === 4 && (
          <div className="mx-auto max-w-md animate-[wizIn_0.25s_ease-out]">
            <style>{`@keyframes wizIn { from { opacity:0; transform: translateY(6px);} to { opacity:1; transform:none;} }`}</style>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-e0e3-muted">Resumen</h3>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between gap-4 border-b border-white/10 pb-2">
                  <dt className="text-e0e3-muted">Nombre</dt>
                  <dd className="text-right font-medium text-e0e3-foreground">{formData.nombre || '—'}</dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-white/10 pb-2">
                  <dt className="text-e0e3-muted">Código</dt>
                  <dd className="font-mono text-xs font-medium text-e0e3-foreground text-right">{formData.codigo || '—'}</dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-white/10 pb-2">
                  <dt className="text-e0e3-muted">Tipo de venta</dt>
                  <dd className="text-right font-medium text-e0e3-foreground">
                    {formData.tipoVentaModo === TIPO_VENTA_MODO_PESO ? 'Peso (kg)' : 'Unidad'}
                  </dd>
                </div>
                {normalizePluBalanza(formData.pluBalanza) && (
                  <div className="flex justify-between gap-4 border-b border-white/10 pb-2">
                    <dt className="text-e0e3-muted">PLU balanza</dt>
                    <dd className="font-mono text-xs font-medium text-e0e3-accent text-right">
                      PLU: {normalizePluBalanza(formData.pluBalanza)}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between gap-4 border-b border-white/10 pb-2">
                  <dt className="text-e0e3-muted">Costo / venta</dt>
                  <dd className="text-right text-e0e3-foreground">
                    ${parseFloat(formData.precio_costo) || 0} / ${parseFloat(formData.precio_venta) || 0}
                  </dd>
                </div>
                <div className="flex justify-between gap-4 border-b border-white/10 pb-2">
                  <dt className="text-e0e3-muted">Listas</dt>
                  <dd className="text-right text-e0e3-foreground">Inician igual al precio de venta</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-e0e3-muted">Activo</dt>
                  <dd className="font-medium text-e0e3-foreground">{formData.activo ? 'Sí' : 'No'}</dd>
                </div>
              </dl>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-white/10 bg-white/[0.02] px-5 py-4 sm:px-6">
        {wizardStep > 1 && (
          <Button color="secondary" type="button" onClick={retrocederWizard} disabled={submitting} icon={<FaArrowLeft />}>
            Atrás
          </Button>
        )}
        {wizardStep < 4 && (
          <Button color="primary" type="button" onClick={avanzarWizard} disabled={submitting}>
            Continuar
          </Button>
        )}
        {wizardStep === 4 && (
          <Button
            color="primary"
            type="button"
            onClick={() => guardarProducto()}
            disabled={submitting}
            icon={submitting ? <FaSpinner className="animate-spin" /> : <FaCheck />}
          >
            {submitting ? 'Guardando…' : 'Guardar producto'}
          </Button>
        )}
      </div>
    </div>
  );
}
